import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    Menu,
    MenuItem,
    CssBaseline,
    useTheme,
    Button,
} from '@mui/material';
import NotificationBell from './NotificationBell';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    People,
    Business,
    Assignment,
    Settings,
    ExitToApp,
    BugReport,
    CreditCard,
    School,
    Assessment,
    TrendingUp,
    VerifiedUser,
    AccountBalanceWallet,
    ChevronLeft,
    ChevronRight,
    Devices,
    Public,
    Upload,
    ContactMail,
    ForwardToInbox,
    Chat,
    AdminPanelSettings,
    PlayCircle,
    DeleteForever,
    DynamicForm,
    Description,
    Gavel,
    Hub,
} from '@mui/icons-material';
import { useDashboardProjectFocus } from '../../context/DashboardProjectFocusContext';

const drawerWidth = 280;

/** Project-scoped links aligned with ProjectDetail tab hashes (role-filtered). */
function getProjectDashboardNavItems(projectId, role) {
    const isClient = role === 'client' || role === 'client_team';
    const isAdmin = ['admin', 'platform_owner', 'platform_test_manager'].includes(role);

    const item = (hash, text, icon) => ({
        text,
        icon,
        path: `/app/projects/${projectId}#${hash}`,
    });

    const items = [
        {
            text: 'Project dashboard',
            icon: <DashboardIcon />,
            path: '/app/dashboard',
            isDashboardHome: true,
        },
    ];

    if (isClient || isAdmin) {
        items.push(item('info', 'Insights', <DashboardIcon />));
        items.push(item('insights', 'Project Wiki', <Description />));
        items.push(item('test-cases', 'Test Repository', <Assignment />));
    }

    items.push(item('cycles', 'Test executions', <PlayCircle />));
    items.push(item('issues', 'Defects', <BugReport />));

    if (isAdmin) {
        items.push(item('triage', 'Issues Triage', <Gavel />));
        items.push(item('team', 'Project Team', <People />));
    }

    if (isClient || isAdmin) {
        items.push(item('reports', 'Reports', <Assessment />));
        items.push(item('integrations', 'Integrations', <Hub />));
    }

    items.push(item('forms', 'Forms', <DynamicForm />));

    return items;
}

const DashboardLayout = ({ children, user: userProp }) => {
    const { user: authUser } = useSelector((state) => state.auth);
    const user = userProp || authUser;
    const theme = useTheme();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileGlobalNavOpen, setMobileGlobalNavOpen] = useState(false);
    const [desktopGlobalNavOpen, setDesktopGlobalNavOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { projectSidebar, exitProjectDashboardMode } = useDashboardProjectFocus();

    // Sidebar visibility state (persisted in localStorage)
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        const saved = localStorage.getItem('sidebarOpen');
        return saved !== null ? JSON.parse(saved) : true;
    });

    // `end: false` so nested routes (e.g. /app/projects/:id/test-executions/:executionId) stay
    // project-scoped and keep the main global sidebar hidden, same as /app/projects/:id.
    const matchAppProject = useMatch({ path: '/app/projects/:id', end: false });
    const matchLegacyProject = useMatch({ path: '/projects/:id', end: false });
    const isStaff = ['admin', 'platform_owner', 'platform_test_manager'].includes(user?.role);
    const hideGlobalRailLegacy =
        Boolean(matchAppProject || matchLegacyProject) && !isStaff;
    const isDashboardPath =
        location.pathname === '/app/dashboard' || location.pathname === '/dashboard';
    const isDashboardProjectScope = Boolean(isDashboardPath && projectSidebar);
    const effectiveSidebarOpen = hideGlobalRailLegacy ? false : sidebarOpen;
    const openGlobalMenuOverlay = hideGlobalRailLegacy || isDashboardProjectScope;
    const sidebarOffsetActive =
        isDashboardProjectScope || (!hideGlobalRailLegacy && effectiveSidebarOpen);

    const permanentDrawerWidth = hideGlobalRailLegacy
        ? 0
        : isDashboardProjectScope
          ? drawerWidth
          : effectiveSidebarOpen
            ? drawerWidth
            : 0;

    useEffect(() => {
        setMobileOpen(false);
        setMobileGlobalNavOpen(false);
        setDesktopGlobalNavOpen(false);
    }, [location.pathname]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleSidebarToggle = () => {
        const newState = !sidebarOpen;
        setSidebarOpen(newState);
        localStorage.setItem('sidebarOpen', JSON.stringify(newState));
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDrawerNav = (path) => {
        navigate(path);
        setMobileOpen(false);
        setDesktopGlobalNavOpen(false);
        setMobileGlobalNavOpen(false);
    };

    const handleProjectSidebarNav = (navItem) => {
        if (navItem.isDashboardHome) {
            exitProjectDashboardMode();
            navigate('/app/dashboard');
        } else {
            navigate(navItem.path);
        }
        setMobileOpen(false);
        setDesktopGlobalNavOpen(false);
        setMobileGlobalNavOpen(false);
    };

    const projectNavItems =
        projectSidebar && user?.role
            ? getProjectDashboardNavItems(projectSidebar.id, user.role)
            : [];

    const projectDrawer =
        projectSidebar && projectNavItems.length > 0 ? (
            <Box
                sx={{
                    height: '100%',
                    bgcolor: 'white',
                    color: '#0f172a',
                    borderRight: '1px solid #e2e8f0',
                }}
            >
                <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, pt: 2, pb: 1 }}>
                    <Box
                        component="img"
                        src="/logo.png"
                        alt="TestGate Logo"
                        sx={{
                            width: 150,
                            height: 56,
                            objectFit: 'contain',
                        }}
                    />
                </Toolbar>
                <Typography
                    variant="caption"
                    sx={{
                        px: 2,
                        pb: 1,
                        color: '#64748b',
                        display: 'block',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                    title={projectSidebar.title}
                >
                    {projectSidebar.title}
                </Typography>
                <Divider sx={{ borderColor: '#e2e8f0' }} />
                <List sx={{ px: 2, py: 2 }}>
                    {projectNavItems.map((item) => {
                        const isUnverified = user && !user.isVerified;
                        const isRestrictedRole =
                            user?.role === 'tester' || user?.role === 'client';
                        const isRestricted = isUnverified && isRestrictedRole;
                        const isDisabled =
                            isRestricted &&
                            item.path !== '/app/dashboard' &&
                            !item.isDashboardHome;

                        let isActive = false;
                        if (item.isDashboardHome) {
                            isActive = isDashboardPath;
                        } else {
                            const [, hash] = item.path.split('#');
                            const projectPath = `/app/projects/${projectSidebar.id}`;
                            isActive =
                                location.pathname === projectPath &&
                                (location.hash === `#${hash}` ||
                                    (!location.hash && hash === 'info'));
                        }

                        return (
                            <ListItem key={`${item.text}-${item.path}`} disablePadding sx={{ mb: 1 }}>
                                <ListItemButton
                                    onClick={() => handleProjectSidebarNav(item)}
                                    disabled={isDisabled}
                                    sx={{
                                        borderRadius: 2,
                                        bgcolor: isActive ? '#f1f5f9' : 'transparent',
                                        '&:hover': {
                                            bgcolor: isDisabled ? 'transparent' : '#f8fafc',
                                        },
                                        opacity: isDisabled ? 0.5 : 1,
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{ color: isActive ? '#ef4444' : '#64748b', minWidth: 40 }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.text}
                                        primaryTypographyProps={{
                                            fontSize: '0.9rem',
                                            fontWeight: isActive ? 600 : 400,
                                            color: isActive ? '#0f172a' : '#64748b',
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
                <Box sx={{ display: { xs: 'block', md: 'none' }, px: 2, pb: 2 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            setMobileOpen(false);
                            setMobileGlobalNavOpen(true);
                        }}
                    >
                        Full application menu
                    </Button>
                </Box>
            </Box>
        ) : null;

    const handleLogout = () => {
        // Store user role before logout in localStorage so ProtectedRoute can use it
        const userRole = user?.role;
        if (userRole) {
            localStorage.setItem('lastUserRole', userRole);
        }
        dispatch(logout()); // Clear Redux state and localStorage (but we'll restore lastUserRole after)
        handleMenuClose();

        // Navigation is handled by ProtectedRoute in App.js based on lastUserRole
        // or defaults to /login if manual navigation is needed for non-protected routes
        if (!userRole) {
            navigate('/login');
        }
    };

    // Define menu items based on roles (Messages is not offered to client org roles)
    const menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/app/dashboard' },
    ];
    if (user?.role !== 'client' && user?.role !== 'client_team') {
        menuItems.push({
            text: user?.role === 'tester' ? 'Chat with Support' : 'Messages',
            icon: <Chat />,
            path: '/app/messages',
        });
    }

    // Testers see Test Cycles instead of Projects
    if (user?.role === 'tester') {
        menuItems.push(
            { text: 'Test Cycles', icon: <Assignment />, path: '/app/test-cycles' },
            { text: 'Test results forms', icon: <DynamicForm />, path: '/app/tester-forms' },
            { text: 'My reported defects', icon: <BugReport />, path: '/app/my-reported-issues' },
            { text: 'My Profile', icon: <People />, path: '/app/tester-profile' },
            { text: 'My Devices', icon: <Devices />, path: '/app/devices' },
            { text: 'Academy', icon: <School />, path: '/app/academy' },
            { text: 'Earnings', icon: <CreditCard />, path: '/app/earnings' }
        );
    } else {
        menuItems.push({ text: 'Projects', icon: <Assignment />, path: '/app/projects' });
    }

    const isOwnerOrAdmin = user?.role === 'platform_owner' || user?.role === 'admin';
    const isTestManager = user?.role === 'platform_test_manager';

    if (isOwnerOrAdmin || isTestManager) {
        menuItems.push(
            { text: 'Platform Admin', icon: <AdminPanelSettings />, path: '/app/admin' },
            { text: 'Test Cycles', icon: <PlayCircle />, path: '/app/test-cycles' },
            { text: 'Registered Testers', icon: <People />, path: '/app/users' },
            { text: 'Import Testers & Devices', icon: <Upload />, path: '/app/admin/import-testers-devices' }
        );

        // Client- and lead-facing areas stay with owners/admins. Test managers
        // run test cycles and tester operations, not the commercial side.
        if (isOwnerOrAdmin) {
            menuItems.push(
                { text: 'Lead Management', icon: <ContactMail />, path: '/app/leads' },
                { text: 'Clients', icon: <Business />, path: '/app/clients' }
            );
        }

        // Job invite emails are sent by platform owners only (API enforces it too).
        if (user?.role === 'platform_owner') {
            menuItems.push({ text: 'Job Invite Emails', icon: <ForwardToInbox />, path: '/app/admin/job-invites' });
        }

        menuItems.push(
            { text: 'Device Coverage', icon: <Devices />, path: '/app/coverage' },
            { text: 'Tester Coverage', icon: <Public />, path: '/app/tester-coverage' },
            { text: 'Identity review', icon: <VerifiedUser />, path: '/app/kyc-review' },
            { text: 'Payout approvals', icon: <AccountBalanceWallet />, path: '/app/payout-approvals' },
            { text: 'Deletion requests', icon: <DeleteForever />, path: '/app/account-deletion-requests' }
        );

        // Billing is client billing — owners/admins only.
        if (isOwnerOrAdmin) {
            menuItems.push({ text: 'Billing', icon: <CreditCard />, path: '/app/billing' });
        }

        menuItems.push({ text: 'Reports', icon: <Assessment />, path: '/app/reports' });

        // Commercial acquisition analytics — owners/admins only (not test managers).
        if (isOwnerOrAdmin) {
            menuItems.push({
                text: 'Acquisition',
                icon: <TrendingUp />,
                path: '/app/reports/acquisition',
            });
        }
    } else if (isStaff) {
        menuItems.push({ text: 'Test Cycles', icon: <PlayCircle />, path: '/app/test-cycles' });
    }

    if (user?.role === 'client' || user?.role === 'client_team') {
        menuItems.push(
            { text: 'Tester Coverage', icon: <Public />, path: '/app/tester-coverage' },
            { text: 'Billing', icon: <CreditCard />, path: '/app/billing' }
        );
    }

    menuItems.push({ text: 'Settings', icon: <Settings />, path: '/app/settings' });

    const drawer = (
        <Box sx={{
            height: '100%',
            bgcolor: 'white',
            color: '#0f172a',
            borderRight: '1px solid #e2e8f0'
        }}>
            <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, pt: 2, pb: 1 }}>
                <Box
                    component="img"
                    src="/logo.png"
                    alt="TestGate Logo"
                    sx={{
                        width: 170,
                        height: 65,
                        objectFit: 'contain',
                    }}
                />

            </Toolbar>
            <Divider sx={{ borderColor: '#e2e8f0' }} />
            <List sx={{ px: 2, py: 2 }}>
                {menuItems.map((item) => {
                    const isActive = location.pathname === item.path;

                    // Restrict navigation for unverified testers and clients
                    const isUnverified = user && !user.isVerified;
                    const isRestrictedRole = user?.role === 'tester' || user?.role === 'client';
                    const isRestricted = isUnverified && isRestrictedRole;

                    // Allow access to Dashboard even if restricted, so they can see the alert
                    // Also allow logout (handled by TopBar) but here strictly sidebar items
                    // We disable everything except maybe Dashboard if we want them to stay there
                    // But usually, if they are on dashboard, clicking dashboard again is fine.
                    // If they are strictly not allowed to navigate AWAY, disable everything else.

                    const isDisabled = isRestricted && item.path !== '/app/dashboard';

                    return (
                        <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                            <ListItemButton
                                onClick={() => handleDrawerNav(item.path)}
                                disabled={isDisabled}
                                sx={{
                                    borderRadius: 2,
                                    bgcolor: isActive ? '#f1f5f9' : 'transparent',
                                    '&:hover': { bgcolor: isDisabled ? 'transparent' : '#f8fafc' },
                                    opacity: isDisabled ? 0.5 : 1,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <ListItemIcon sx={{ color: isActive ? '#ef4444' : '#64748b', minWidth: 40 }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.text}
                                    primaryTypographyProps={{
                                        fontSize: '0.9rem',
                                        fontWeight: isActive ? 600 : 400,
                                        color: isActive ? '#0f172a' : '#64748b'
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            {/* Top App Bar */}
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    width: {
                        md: sidebarOffsetActive ? `calc(100% - ${drawerWidth}px)` : '100%',
                        transition: theme.transitions.create(['width', 'margin'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    },
                    ml: { md: sidebarOffsetActive ? `${drawerWidth}px` : 0 },
                    bgcolor: 'white',
                    borderBottom: '1px solid #e2e8f0',
                    color: '#0f172a'
                }}
            >
                <Toolbar sx={{ minHeight: { xs: 56, md: 60 }, px: { xs: 2, md: 3 } }}>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* Project detail or dashboard project scope: open global nav as overlay */}
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={() => setDesktopGlobalNavOpen(true)}
                        sx={{
                            mr: 2,
                            display: { xs: 'none', md: openGlobalMenuOverlay ? 'flex' : 'none' },
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                        }}
                        aria-label="Open main menu"
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* Desktop: collapse/expand permanent global rail */}
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleSidebarToggle}
                        sx={{
                            mr: 2,
                            display: { xs: 'none', md: openGlobalMenuOverlay ? 'none' : 'flex' },
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
                    </IconButton>

                    <Box sx={{ flexGrow: 1 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {isDashboardProjectScope && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    exitProjectDashboardMode();
                                    navigate('/app/dashboard');
                                }}
                                sx={{
                                    borderColor: '#e2e8f0',
                                    color: '#0f172a',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Main dashboard
                            </Button>
                        )}
                        <NotificationBell />
                        <Typography variant="body2" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                            {user?.firstName} {user?.lastName} ( {user?.role?.replace('_', ' ')} )
                        </Typography>
                        <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
                            <Avatar
                                src={user?.profilePictureUrl}
                                sx={{ bgcolor: '#cc0000' }}
                            >
                                {!user?.profilePictureUrl && (user?.firstName?.charAt(0) || 'U')}
                            </Avatar>
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                                    mt: 1.5,
                                    minWidth: 180
                                },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem onClick={() => {
                                handleMenuClose();
                                navigate(user?.role === 'tester' ? '/app/tester-profile' : '/app/settings');
                            }}>My Profile</MenuItem>
                            <MenuItem onClick={() => {
                                handleMenuClose();
                                navigate('/app/settings');
                            }}>Account Settings</MenuItem>
                            <Divider />
                            <MenuItem onClick={handleLogout} sx={{ color: '#ef4444' }}>
                                <ListItemIcon>
                                    <ExitToApp fontSize="small" sx={{ color: '#ef4444' }} />
                                </ListItemIcon>
                                Logout
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Sidebar Drawer */}
            <Box
                component="nav"
                sx={{
                    width: { md: sidebarOffsetActive ? drawerWidth : 0 },
                    flexShrink: { md: 0 },
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                {/* Mobile: project rail or global rail */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                >
                    {isDashboardProjectScope && projectDrawer ? projectDrawer : drawer}
                </Drawer>
                <Drawer
                    variant="temporary"
                    open={mobileGlobalNavOpen}
                    onClose={() => setMobileGlobalNavOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                >
                    {drawer}
                </Drawer>
                {/* Desktop: global nav overlay */}
                <Drawer
                    variant="temporary"
                    open={desktopGlobalNavOpen}
                    onClose={() => setDesktopGlobalNavOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'none', md: openGlobalMenuOverlay ? 'block' : 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                >
                    {drawer}
                </Drawer>
                {/* Desktop permanent rail: global menu or project-scoped dashboard menu */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: {
                            xs: 'none',
                            md: hideGlobalRailLegacy && !isDashboardProjectScope ? 'none' : 'block',
                        },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: permanentDrawerWidth,
                            borderRight: permanentDrawerWidth ? '1px solid #e2e8f0' : 'none',
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                            overflowX: 'hidden',
                        },
                    }}
                >
                    {isDashboardProjectScope && projectDrawer
                        ? projectDrawer
                        : !hideGlobalRailLegacy && effectiveSidebarOpen
                          ? drawer
                          : null}
                </Drawer>
            </Box>

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 1.5, md: hideGlobalRailLegacy ? 2 : 3 },
                    width: {
                        md: sidebarOffsetActive ? `calc(100% - ${drawerWidth}px)` : '100%',
                        transition: theme.transitions.create(['width', 'margin'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    },
                    minHeight: '100vh',
                    bgcolor: '#f8fafc'
                }}
            >
                <Toolbar sx={{ minHeight: { xs: 56, md: 60 } }} /> {/* Spacer for AppBar */}
                {children}

                {/* Footer */}
                <Box
                    component="footer"
                    sx={{
                        mt: 'auto',
                        pt: 1,
                        pb: 1,
                        borderTop: '1px solid #e2e8f0',
                        textAlign: 'center',
                        bgcolor: '#ffffff'
                    }}
                >
                    <Typography
                        variant="body2"
                        component="div"
                        sx={{
                            color: '#64748b',
                            fontSize: '0.875rem',
                            lineHeight: 1.8,
                        }}
                    >
                        <a
                            href="https://apptestify.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#64748b',
                                textDecoration: 'none',
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#cc0000'}
                            onMouseLeave={(e) => e.target.style.color = '#64748b'}
                        >
                            AppTestify Global Services Private Limited
                        </a>
                        <Box component="span" sx={{ mx: 1.5, color: '#cbd5e1' }}>|</Box>
                        CIN: U74999DL2021PTC382674
                        <Box component="span" sx={{ mx: 1.5, color: '#cbd5e1' }}>|</Box>
                        © 2025 AppTestify. All rights reserved.
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#94a3b8',
                            fontSize: '0.75rem',
                            fontStyle: 'italic',
                            mt: 1,
                        }}
                    >
                        TestGate is a crowdtesting platform of AppTestify
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default DashboardLayout;
